import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './MembershipTransactionSidePanel.module.css';
import PrimiumMemberIcon from '../Icons/PrimiumMemberIcon';
import CalendarIcon from '../Icons/CalendarIcon';
import UserIcon from '../Icons/UserIcon';
import menuIcon from '../../Assets/images/menu.svg';
import editIcon from '../../Assets/images/EditIcon.svg';
import BackIcon from '../../Assets/images/back.svg';

/**
 * Slide-in panel from the right (same interaction pattern as purchase / client sidebars).
 * Content area is intentionally empty until wired to API data.
 */
const formatDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

/** Bold-friendly line: "Mon, Jan 5, 2:23 PM" (matches reference sale details). */
const formatDateTimeDetailed = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

const formatAmount = (value) => {
    const amount = Number(value || 0);
    if (!Number.isFinite(amount)) return '0.00';
    return amount.toFixed(2);
};

const formatExpiryCurrency = (value) => {
    const amount = Number(value || 0);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    return `-$${Math.abs(safeAmount).toFixed(2)}`;
};

const formatPositiveCurrency = (value) => {
    const amount = Number(value || 0);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    return `$${Math.abs(safeAmount).toFixed(2)}`;
};

const formatSignedCurrency = (value) => {
    const amount = Number(value || 0);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    const prefix = safeAmount < 0 ? '-$' : '$';
    return `${prefix}${Math.abs(safeAmount).toFixed(2)}`;
};

const parseMoneyValue = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
};

const pickFirstNonZeroMoney = (...values) => {
    for (const value of values) {
        const amount = parseMoneyValue(value);
        if (amount != null && Math.abs(amount) > 0.0001) {
            return amount;
        }
    }
    return null;
};

/** e.g. "Mon, Jan 05, 2026 at 2:23 PM" (payment detail sheet). */
const formatDateTimeWithAt = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    const datePart = parsed.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    });
    const timePart = parsed.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${datePart} at ${timePart}`;
};

const formatExpiryDateTime = (value) => formatDateTimeDetailed(value).replace(/:(\d{2})\s/, '.$1 ');

const formatExpiryShortDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const getInitials = (name) => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'NA';
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('');
};

const getConsistentColor = (userId, name = '') => {
    const seed = userId ? String(userId) : (name || 'default');
    let hash = 0;
    for (let index = 0; index < seed.length; index += 1) {
        const char = seed.charCodeAt(index);
        hash = ((hash << 5) - hash) + char;
        hash &= hash;
    }
    const r = Math.abs(hash % 120) + 80;
    const g = Math.abs((hash * 7) % 120) + 80;
    const b = Math.abs((hash * 13) % 120) + 80;
    return `rgb(${r},${g},${b})`;
};

/** API may put "$500" in package title; product copy is always this label. */
const receiptLineLabel = 'Cashless Package Top Up';

const isMembershipCreditPaymentMethod = (methodLabel) => {
    const normalized = String(methodLabel || '').trim().toLowerCase();
    return normalized.includes('membership credit');
};

const isCashVoucherPaymentSlug = (payment) =>
    String(payment?.payment_method_slug || '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_') === 'cash_voucher';

/** API may return typo'd `redemed_ammount`; accept common variants. */
const getCashVoucherRedeemedAmount = (payment) =>
    payment?.redemed_ammount ??
    payment?.redeemed_amount ??
    payment?.redeemed_ammount ??
    null;

const formatGracePeriodReductionDisplay = (value) => {
    if (value == null || value === '') return 'Not Applicable';
    const amount = Number(value);
    if (!Number.isFinite(amount) || Math.abs(amount) < 0.0001) return 'Not Applicable';
    return formatPositiveCurrency(amount);
};

const isOnlineWebPayment = (payment) =>
    String(payment?.panel || '').trim().toLowerCase() === 'customer';

const isAdyenPaymentMethod = (methodLabel) =>
    String(methodLabel || '').trim().toLowerCase() === 'adyen';

const getAdyenPspReference = (payment) =>
    String(
        payment?.adyen_psp_reference ??
        payment?.psp_reference ??
        payment?.original_reference ??
        payment?.payment_reference ??
        ''
    ).trim();

const isRefundPaymentRowByCode = (payment) =>
    String(payment?.result_code ?? '').trim().toLowerCase() === 'refund requested';

const getPaymentRowTransactionId = (payment) => {
    if (!payment || typeof payment !== 'object') return null;
    const id = payment.transaction_id ?? payment.payment_transaction_id ?? payment.payment_id ?? null;
    return id != null && String(id).trim() !== '' ? id : null;
};

const getParentTransactionId = (payment) => {
    const parentId = payment?.parent_transaction_id ?? payment?.parent_payment_transaction_id ?? null;
    if (parentId == null || String(parentId).trim() === '') return null;
    return String(parentId);
};

const findLinkedRefundPayment = (payment, allPayments) => {
    const parentTxnId = getPaymentRowTransactionId(payment);
    if (!parentTxnId || isRefundPaymentRowByCode(payment) || !Array.isArray(allPayments)) return null;
    return (
        allPayments.find(
            (row) =>
                isRefundPaymentRowByCode(row) &&
                getParentTransactionId(row) === String(parentTxnId)
        ) ?? null
    );
};

const mergePaymentRowsForRefundLink = (...paymentLists) => {
    const seen = new Set();
    return paymentLists.flatMap((list) => (Array.isArray(list) ? list : [])).filter((payment) => {
        const id = getPaymentRowTransactionId(payment);
        if (id == null) return true;
        const key = String(id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const isMeaningfulPaymentRow = (payment) => {
    if (!payment || typeof payment !== 'object') return false;
    const methodLabel = String(payment?.payment_method_name || payment?.payment_method || '').trim();
    const hasMethod = methodLabel !== '' && methodLabel !== '-';
    const amount = Number(payment?.amount_value ?? payment?.amount);
    const hasAmount = Number.isFinite(amount) && Math.abs(amount) > 0.0001;
    const hasId =
        payment?.id != null &&
        String(payment.id).trim() !== '' &&
        String(payment.id).trim().toLowerCase() !== 'null';
    return hasMethod || hasAmount || hasId;
};

const getGiftReturnStatus = (gift) => {
    const rawStatus = String(
        gift?.return_status ??
        gift?.returned_status ??
        gift?.status ??
        ''
    ).trim().toLowerCase();
    const rawFlag =
        gift?.is_returned ??
        gift?.returned ??
        gift?.isReturned ??
        gift?.is_return;
    if (rawFlag === true || rawFlag === 1 || rawFlag === '1' || rawStatus === 'returned') {
        return 'Returned';
    }
    if (rawFlag === false || rawFlag === 0 || rawFlag === '0' || rawStatus === 'not returned') {
        return 'Not Returned';
    }
    return '';
};

const getGiftDisplayName = (gift) =>
    gift?.name || gift?.gift_name || gift?.title || gift?.remarks || '-';

/** Nested drawer from payment row "View Details". */
export const MembershipTxnPaymentDetailSheet = ({
    detail,
    selectedPayment,
    onClose,
    onGoToSale,
    stackWidth,
    paymentTransactionDetail = null,
    saleId = null,
    loading = false,
    refundDate = null,
    allPayments = [],
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isHomeRoute = location.pathname === '/';
    const isBookingPaymentTransaction = Boolean(paymentTransactionDetail);
    const hasNestedTransaction = Boolean(detail?.membership_transaction) && !isBookingPaymentTransaction;
    const flatTransaction =
        !hasNestedTransaction && detail && typeof detail === 'object'
            ? detail
            : null;
    const transaction = detail?.membership_transaction || flatTransaction || {};
    const payments = Array.isArray(transaction?.payments)
        ? transaction.payments
        : (Array.isArray(flatTransaction?.payments) ? flatTransaction.payments : []);
    const firstPayment = payments[0] || {};
    const allPaymentsForRefundLink = mergePaymentRowsForRefundLink(
        payments,
        allPayments,
        detail?.payments,
        flatTransaction?.payments
    );
    const paymentForDetails = isBookingPaymentTransaction
        ? paymentTransactionDetail
        : ((selectedPayment && typeof selectedPayment === 'object') ? selectedPayment : firstPayment);
    const normalizeReferenceId = (value) =>
        value != null && String(value).trim() !== ''
            ? String(value).replace(/^#/, '').trim()
            : '';
    const createdBy = paymentForDetails?.created_by_name || transaction?.created_by_name || '-';
    const createdAt = paymentForDetails?.created_at || transaction?.created_at || null;
    const resolvedRefundDate =
        refundDate ??
        paymentForDetails?.updated_at ??
        transaction?.updated_at ??
        detail?.updated_at ??
        createdAt;
    const actionLabel = transaction?.action_label || transaction?.action || 'Top-up';
    const saleSearchId =
        paymentForDetails?.sale_id ??
        saleId ??
        transaction?.sale_id ??
        detail?.sale_id ??
        null;

    const saleDisplayRaw = normalizeReferenceId(saleSearchId);

    const childSaleReference = normalizeReferenceId( paymentForDetails?.child_sale_id ?? paymentForDetails?.__childSaleId ??transaction?.child_sale_id ?? detail?.child_sale_id);
    const parentSaleReference = normalizeReferenceId( paymentForDetails?.parent_sale_id ?? paymentForDetails?.__parentSaleId ?? transaction?.parent_sale_id ?? detail?.parent_sale_id );
    const currentSaleReference = normalizeReferenceId( transaction?.sale_id ?? null );
    const hasRelatedRefundSale = Boolean(childSaleReference || parentSaleReference);
    const isRefundDetailLayout = hasRelatedRefundSale && Boolean(paymentForDetails?.__isRefundPayment || paymentForDetails?.__isRefundRow || parentSaleReference);
    const showOriginalSaleRefundDetails = hasRelatedRefundSale && !isRefundDetailLayout && Boolean(childSaleReference);
    const displaySaleReference = isRefundDetailLayout
        ? (childSaleReference || currentSaleReference || saleDisplayRaw)
        : (currentSaleReference || saleDisplayRaw);
    const rawAction = String(transaction?.action || '').trim().toLowerCase();
    const isTopup = rawAction === 'topup' || rawAction === 'top_up' || String(actionLabel).toLowerCase().includes('top-up') || String(actionLabel).toLowerCase().includes('top up');
    const defaultTxRefRaw = transaction?.transaction_number ?? transaction?.txn_reference ?? transaction?.reference ?? transaction?.transaction_code ?? transaction?.transaction_id ?? transaction?.id ??'';
    const paymentRowTransactionId = getPaymentRowTransactionId(paymentForDetails);
    const txRefRaw = paymentRowTransactionId ?? (isTopup ? paymentForDetails?.id : null) ?? defaultTxRefRaw;
    const headerTitle = (() => {
        if (txRefRaw === '' || txRefRaw == null) {
            return saleDisplayRaw ? `TX${saleDisplayRaw}` : 'Transaction';
        }
        const s = String(txRefRaw);
        return s.toUpperCase().startsWith('TX') ? s : `TX${s}`;
    })();
    const typeLabel = isBookingPaymentTransaction
        ? (paymentForDetails?.payment_method || 'Payment')
        : (
            transaction?.type_label ||
            transaction?.transaction_type_label ||
            transaction?.transaction_type ||
            transaction?.type ||
            'Payment'
        );
    const paymentMethodLabel =
        paymentForDetails?.payment_method_name ||
        paymentForDetails?.payment_method ||
        transaction?.payment_method_name ||
        '-';
    // const paymentChannelLabel =
    //     paymentForDetails?.created_by_name ||
    //     paymentForDetails?.payment_channel_name ||
    //     paymentForDetails?.channel_name ||
    //     transaction?.payment_channel_name ||
    //     createdBy ||
    //     'Staff member';
    // const showPaymentChannel = !isOnlineWebPayment(paymentForDetails);
    const paymentChannelLabel = paymentForDetails?.created_by_name ||   'Online';
    const showAdyenPspReference = isAdyenPaymentMethod(paymentMethodLabel);
    const adyenPspReference = getAdyenPspReference(paymentForDetails);
    const skipPasswordReason = String(paymentForDetails?.skip_password_reason ?? '').trim();
    const showPasswordBypass = isBookingPaymentTransaction && skipPasswordReason !== '';
    const isCashVoucherPayment = isCashVoucherPaymentSlug(paymentForDetails);
    const cashVoucherCode = String(paymentForDetails?.voucher_code ?? '').trim();
    const cashVoucherValue = paymentForDetails?.voucher_value;
    const cashRedeemedAmount = getCashVoucherRedeemedAmount(paymentForDetails);
    const cashExcessFromVoucher = paymentForDetails?.excess_from_cash_voucher;
    const cashGracePeriodReduction = paymentForDetails?.grace_period_reduction;
    const amountForDetails = Number(
        paymentForDetails?.amount_value ??
        paymentForDetails?.amount ??
        transaction?.amount ??
        transaction?.grand_total
    );
    const paymentOpeningBalance = Number(paymentForDetails?.balance_before ?? transaction?.balance_before);
    const paymentClosingBalance = Number(paymentForDetails?.balance_after ?? transaction?.balance_after);
    const hasPaymentBalanceData =
        Number.isFinite(paymentOpeningBalance) || Number.isFinite(paymentClosingBalance);
    const showPaymentBalanceInfo =
        !isRefundDetailLayout && isMembershipCreditPaymentMethod(paymentMethodLabel) && hasPaymentBalanceData;
    const amountDisplay = isRefundDetailLayout
        ? formatSignedCurrency(-Math.abs(Number.isFinite(amountForDetails) ? amountForDetails : 0))
        : formatPositiveCurrency(amountForDetails);
    const linkedRefundPayment =
        paymentForDetails?.__linkedRefundPayment ??
        findLinkedRefundPayment(paymentForDetails, allPaymentsForRefundLink);
    const linkedRefundAmount = Number(
        linkedRefundPayment?.amount_value ??
        linkedRefundPayment?.amount ??
        amountForDetails
    );
    const linkedRefundAmountDisplay = formatPositiveCurrency(linkedRefundAmount);
    const linkedRefundSignedAmountDisplay = formatSignedCurrency(
        -Math.abs(Number.isFinite(linkedRefundAmount) ? linkedRefundAmount : 0)
    );
    const linkedRefundDate =
        linkedRefundPayment?.created_at ??
        linkedRefundPayment?.updated_at ??
        resolvedRefundDate;
    const refundTransactionReference = normalizeReferenceId(
        (showOriginalSaleRefundDetails || isRefundDetailLayout) && linkedRefundPayment
            ? getPaymentRowTransactionId(linkedRefundPayment)
            : isRefundDetailLayout || isRefundPaymentRowByCode(paymentForDetails)
                ? getPaymentRowTransactionId(paymentForDetails)
                : (
                    getPaymentRowTransactionId(paymentForDetails) ??
                    transaction?.transaction_id
                )
    );

    const handleGoToSale = () => {
        const idForSales = displaySaleReference || saleSearchId || transaction?.id;
        if (idForSales == null || String(idForSales).trim() === '') {
            return;
        }
        onClose?.();
        window.setTimeout(() => {
            if (typeof onGoToSale === 'function') {
                onGoToSale(idForSales);
                return;
            }
            navigate(`/sales?search=${encodeURIComponent(idForSales)}`);
        }, 0);
    };

    const nestedStackStyle = stackWidth
        ? { '--sale-stack-width': stackWidth }
        : undefined;

    return (
        <div
            className={`${styles.nestedStackRoot} ${isHomeRoute ? styles.nestedStackRootBelowHeader : ''}`}
            style={nestedStackStyle}
        >
            <button
                type="button"
                className={styles.nestedStackDim}
                onClick={onClose}
                aria-label="Dismiss"
            />
            <button
                type="button"
                className={styles.nestedStackPeek}
                onClick={onClose}
                aria-label="Close transaction details"
            />
            <aside
                className={styles.nestedPaymentPanel}
                onClick={(event) => event.stopPropagation()}
                aria-label="Payment transaction details"
            >
                <header className={styles.nestedPaymentHeader}>
                    <div className={styles.nestedPaymentHeaderLeft}>
                        <button type="button" className={styles.nestedBackButton} onClick={onClose} aria-label="Back">
                            <img src={BackIcon} alt="" />
                        </button>
                        <h2 className={styles.nestedPaymentTitle}>{headerTitle}</h2>
                    </div>
                </header>
                <div className={styles.nestedPaymentBody}>
                    <div className={styles.nestedPaymentScroll}>
                        {loading ? (
                            <p className={styles.paymentDetailLoading}>Loading...</p>
                        ) : (
                        <>
                        <div className={styles.paymentDetailIntro}>
                            <div className={styles.paymentDetailTopupLabel}>
                                {isRefundDetailLayout ? 'Refund' : (isBookingPaymentTransaction ? 'Sale' : String(actionLabel || 'Top-up'))}
                            </div>
                            <div className={styles.paymentDetailTopupRow}>
                                <span className={styles.paymentDetailSaleHash}>#{displaySaleReference || '—'}</span>
                                <button type="button" className={styles.paymentDetailGoSale} onClick={handleGoToSale}>
                                    (Go to sale)
                                </button>
                            </div>
                        </div>
                        <div className={styles.paymentDetailKv}>
                            <span className={styles.paymentDetailFieldLabel}>Type</span>
                            <span className={styles.paymentDetailFieldValue}>{paymentMethodLabel}</span>
                        </div>
                        {isCashVoucherPayment ? (
                            <>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Voucher Code</span>
                                    <span className={styles.paymentDetailFieldValue}>
                                        {cashVoucherCode || '—'}
                                    </span>
                                </div>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Voucher Value</span>
                                    <span className={styles.paymentDetailFieldValue}>
                                        {formatPositiveCurrency(cashVoucherValue)}
                                    </span>
                                </div>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Redeemed Amount</span>
                                    <span className={styles.paymentDetailFieldValue}>
                                        {formatPositiveCurrency(cashRedeemedAmount)}
                                    </span>
                                </div>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Excess from Cash Voucher</span>
                                    <span className={styles.paymentDetailFieldValue}>
                                        {formatPositiveCurrency(cashExcessFromVoucher)}
                                    </span>
                                </div>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Grace Period Reduction</span>
                                    <span className={styles.paymentDetailFieldValue}>
                                        {formatGracePeriodReductionDisplay(cashGracePeriodReduction)}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <div className={styles.paymentDetailKv}>
                                <span className={styles.paymentDetailFieldLabel}>Amount</span>
                                <span className={styles.paymentDetailFieldValue}>{amountDisplay}</span>
                            </div>
                        )}
                        {showOriginalSaleRefundDetails ? (
                            <div className={styles.paymentDetailKv}>
                                <span className={styles.paymentDetailFieldLabel}>Amount Refunded</span>
                                <span className={styles.paymentDetailFieldValue}>{linkedRefundAmountDisplay}</span>
                            </div>
                        ) : null}
                        {isRefundDetailLayout ? null : (
                            <div className={styles.paymentDetailKv}>
                                <span className={styles.paymentDetailFieldLabel}>Date/Time</span>
                                <span className={styles.paymentDetailFieldValue}>{formatDateTimeWithAt(createdAt)}</span>
                            </div>
                        )}
                        {isRefundDetailLayout ? (
                            <>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Date/Time</span>
                                    <span className={styles.paymentDetailFieldValue}>{formatDateTimeWithAt(resolvedRefundDate)}</span>
                                </div>
                                <div className={styles.paymentDetailDivider} aria-hidden="true" />
                                <h3 className={styles.paymentDetailsSectionTitle}>Refund Details</h3>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Transaction ID</span>
                                    <span className={styles.paymentDetailFieldValue}>
                                        #{getPaymentRowTransactionId(paymentForDetails) || refundTransactionReference || '—'}
                                    </span>
                                </div>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Refund Method</span>
                                    <span className={styles.paymentDetailFieldValue}>{paymentMethodLabel}</span>
                                </div>
                            </>
                        ) : (
                        <>
                        <div className={styles.paymentDetailDivider} aria-hidden="true" />
                        <h3 className={styles.paymentDetailsSectionTitle}>Payment Details</h3>
                        <div className={styles.paymentDetailKv}>
                            <span className={styles.paymentDetailFieldLabel}>Payment Method</span>
                            <span className={styles.paymentDetailFieldValue}>{paymentMethodLabel}</span>
                            {showPaymentBalanceInfo && (
                                <span className={styles.paymentDetailSubInfo}>
                                    {/* <span>Opening Balance: ${formatAmount(paymentOpeningBalance)}</span> */}
                                    <span>Closing Balance: ${formatAmount(paymentClosingBalance)}</span>
                                </span>
                            )}
                        </div>
                        {showAdyenPspReference ? (
                            <div className={styles.paymentDetailKv}>
                                <span className={styles.paymentDetailFieldLabel}>Adyen PSP Reference</span>
                                <span className={styles.paymentDetailFieldValue}>{adyenPspReference || '—'}</span>
                            </div>
                        ) : null}
                        {/* {showPaymentChannel ? (
                            <div className={styles.paymentDetailKv}>
                                <span className={styles.paymentDetailFieldLabel}>Payment Channel</span>
                                <span className={styles.paymentDetailFieldValue}>{paymentChannelLabel}</span>
                            </div>
                        ) : null} */}
                        <div className={styles.paymentDetailKv}>
                            <span className={styles.paymentDetailFieldLabel}>Payment Channel</span>
                            <span className={styles.paymentDetailFieldValue}>{paymentChannelLabel}</span>
                        </div>
                        {showPasswordBypass ? (
                            <>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Password</span>
                                    <span className={styles.paymentDetailFieldValue}>
                                        {`Bypassed by ${createdBy || 'Staff member'}`}
                                    </span>
                                </div>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Reason</span>
                                    <span className={styles.paymentDetailFieldValue}>{skipPasswordReason}</span>
                                </div>
                            </>
                        ) : null}
                        {showOriginalSaleRefundDetails ? (
                            <>
                                <div className={styles.paymentDetailDivider} aria-hidden="true" />
                                <h3 className={styles.paymentDetailsSectionTitle}>Refund Details</h3>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Transaction ID</span>
                                    <span className={styles.paymentDetailFieldValue}>
                                        #{refundTransactionReference || '—'}
                                    </span>
                                </div>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Amount</span>
                                    <span className={styles.paymentDetailFieldValue}>{linkedRefundSignedAmountDisplay}</span>
                                </div>
                                <div className={styles.paymentDetailKv}>
                                    <span className={styles.paymentDetailFieldLabel}>Date/Time</span>
                                    <span className={styles.paymentDetailFieldValue}>{formatDateTimeWithAt(linkedRefundDate)}</span>
                                </div>
                            </>
                        ) : null}
                        </>
                        )}
                        </>
                        )}
                    </div>
                </div>
            </aside>
        </div>
    );
};

const MembershipTransactionSidePanel = ({ onClose, onGoToSale, title, detail, children, saleNote, onAddNote, footer }) => {
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const [openPaymentMenuKey, setOpenPaymentMenuKey] = useState(null);
    const paymentMenuRef = useRef(null);
    const [isPaymentDetailSheetOpen, setIsPaymentDetailSheetOpen] = useState(false);
    const [selectedPaymentDetail, setSelectedPaymentDetail] = useState(null);

    const detailKey = detail?.membership_transaction?.id ?? detail?.id ?? null;
    useEffect(() => {
        setIsPaymentDetailSheetOpen(false);
        setSelectedPaymentDetail(null);
    }, [detailKey]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
            if (paymentMenuRef.current && !paymentMenuRef.current.contains(event.target)) {
                setOpenPaymentMenuKey(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const hasNestedTransaction = Boolean(detail?.membership_transaction);
    const flatTransaction =
        !hasNestedTransaction && detail && typeof detail === 'object'
            ? detail
            : null;
    const transaction = detail?.membership_transaction || flatTransaction || {};
    const membershipDetail = detail?.membership_detail || (
        flatTransaction
            ? {
                id: flatTransaction?.membership_id,
                membership_id: flatTransaction?.membership_id,
                membership_number: flatTransaction?.membership_number,
                user_id: flatTransaction?.user_id,
                user_name: flatTransaction?.user_name,
                user_phone: flatTransaction?.user_phone ?? flatTransaction?.user_contact_number,
            }
            : {}
    );
    const payments = Array.isArray(transaction?.payments)
        ? transaction.payments
        : (Array.isArray(flatTransaction?.payments) ? flatTransaction.payments : []);
    const displayPayments = payments.filter(isMeaningfulPaymentRow);
    const syntheticPayments = displayPayments.length === 0 && (transaction?.payment_type || flatTransaction?.payment_type)
        ? [{
            payment_method_name: transaction?.payment_type ?? flatTransaction?.payment_type,
            payment_method: transaction?.payment_type ?? flatTransaction?.payment_type,
            amount_value: transaction?.grand_total ?? flatTransaction?.grand_total ?? transaction?.amount ?? flatTransaction?.amount
          }]
        : [];
    const effectiveDisplayPayments = displayPayments.length > 0 ? displayPayments : syntheticPayments;
    const hasPaymentTransactions = effectiveDisplayPayments.length > 0;
    const firstPayment = displayPayments[0] || payments[0] || {};
    const displayName = membershipDetail?.user_name || '-';
    const displayPhone = membershipDetail?.user_phone || '-';
    const hasMembershipBadge = Boolean(
        (membershipDetail && (membershipDetail?.id || membershipDetail?.membership_id || membershipDetail?.membership_number)) ||
        transaction?.user?.membership === 1 ||
        detail?.user?.membership === 1 ||
        transaction?.customer?.membership === 1 ||
        detail?.customer?.membership === 1
    );
    const actionLabel = transaction?.action_label || transaction?.action || 'Transaction';
    const createdBy = transaction?.created_by_name || '-';
    const createdAt = transaction?.created_at || null;
    const updatedBy = transaction?.updated_by_name || '-';
    const updatedAt = transaction?.updated_at || transaction?.created_at || null;
    const resolvedPaymentStatus = String(
        transaction?.payment_status ||
        transaction?.status ||
        detail?.payment_status ||
        detail?.status ||
        ''
    ).toLowerCase();
    const statusSuffix = (resolvedPaymentStatus === 'paid' || resolvedPaymentStatus === 'closed') ? ' (Closed)' : '';
    const titleIdMatch = typeof title === 'string' ? title.match(/#\s*[\w-]+/) : null;
    const titleIdSuffix = titleIdMatch ? ` ${titleIdMatch[0].replace(/\s+/g, '')}` : '';
    const memberName =
        transaction?.user_name ||
        membershipDetail?.user_name ||
        (displayName !== '-' ? displayName : '') ||
        transaction?.membership_package_title ||
        '-';
    const isReinstatement = String(actionLabel).toLowerCase().includes('reinstatement');
    const isExpiry = String(actionLabel).toLowerCase().includes('expiry') || String(actionLabel).toLowerCase().includes('expired');
    const isRefund = String(actionLabel).toLowerCase().includes('refund');
    const isUtilisation = String(actionLabel).toLowerCase().includes('utilisation') || String(actionLabel).toLowerCase().includes('utilization');
    const isAdjustment = String(actionLabel).toLowerCase().includes('adjustment');
    const hasCreatedBy = createdBy && String(createdBy).trim() !== '' && createdBy !== '-';
    const hasUpdatedBy = updatedBy && String(updatedBy).trim() !== '' && updatedBy !== '-';
    const displayUpdatedBy = hasUpdatedBy ? updatedBy : (isAdjustment && hasCreatedBy ? createdBy : '-');
    const isMembershipPackage = String(actionLabel).toLowerCase().includes('package');
    const hasGifts = Array.isArray(transaction?.gifts) && transaction.gifts.length > 0;
    const rawAction = String(transaction?.action || '').trim().toLowerCase();
    const isTopup =
        rawAction === 'topup' ||
        rawAction === 'top_up' ||
        String(actionLabel).toLowerCase().includes('top-up') ||
        String(actionLabel).toLowerCase().includes('top up');
    /** Receipt-style panel (sales sidebar) for membership top-up sale links — includes rows with gifts from API. */
    const showSaleReceiptLayout =
        isTopup &&
        !isRefund &&
        !isUtilisation &&
        !isReinstatement &&
        !isExpiry;
    const showTotalAndPaymentsFooter =
        !(isReinstatement || isExpiry || isAdjustment || isMembershipPackage || hasGifts);
    const showSummaryBeforeFinancialFooter =
        !(isRefund || isUtilisation) && showTotalAndPaymentsFooter;
    const parseMoney = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    };
    const displayActionLabel = isExpiry ? 'Membership Credits' : (isReinstatement ? 'Credit Reinstatement' : actionLabel);
    const expiryCreditAmount = parseMoney(
        transaction?.amount ??
        transaction?.grand_total ??
        transaction?.balance_before ??
        firstPayment?.amount_value ??
        firstPayment?.amount
    );
    const expiryClosingBalance = parseMoney(
        firstPayment?.balance_after ??
        transaction?.balance_after
    );
    const expiryReference =
        membershipDetail?.membership_number ||
        membershipDetail?.membership_id ||
        membershipDetail?.user_id ||
        transaction?.membership_id ||
        transaction?.user_id ||
        '';
    const expiryTransactionLabel = expiryReference
        ? `Membership Credit (#${String(expiryReference).replace(/^#/, '')})`
        : 'Membership Credit';
    const reinstatementCreditAmount = parseMoney(
        transaction?.amount ??
        transaction?.grand_total ??
        firstPayment?.amount_value ??
        firstPayment?.amount ??
        transaction?.balance_after
    );
    const reinstatementExpiredCredit = parseMoney(
        transaction?.expired_credit ??
        transaction?.expired_credit_amount ??
        transaction?.expired_amount ??
        transaction?.amount ??
        transaction?.grand_total
    );
    const reinstatementClosingBalance = parseMoney(
        firstPayment?.balance_after ??
        transaction?.balance_after ??
        reinstatementCreditAmount
    );
    const adjustmentOpeningBalance = parseMoney(
        firstPayment?.balance_before ??
        transaction?.balance_before ??
        transaction?.opening_balance ??
        transaction?.credit_balance_before
    );
    const adjustmentClosingBalance = parseMoney(
        firstPayment?.balance_after ??
        transaction?.balance_after ??
        transaction?.closing_balance ??
        transaction?.credit_balance_after
    );
    const adjustmentDelta = adjustmentClosingBalance - adjustmentOpeningBalance;
    const adjustmentAmount =
        pickFirstNonZeroMoney(
            transaction?.credit_delta,
            transaction?.adjustment_amount,
            transaction?.amount_value,
            transaction?.grand_total,
            transaction?.amount,
            firstPayment?.amount_value,
            firstPayment?.amount,
            adjustmentDelta
        ) ?? 0;
    const resolvedNote = String(
        saleNote ??
        transaction?.notes ??
        transaction?.note ??
        detail?.notes ??
        detail?.note ??
        ''
    ).trim();
    const adjustmentReason = String(
        transaction?.adjustment_reason ??
        transaction?.reason ??
        transaction?.reason_text ??
        transaction?.remarks ??
        transaction?.remark ??
        detail?.adjustment_reason ??
        detail?.reason ??
        resolvedNote ??
        ''
    ).trim();
    const refundSaleReference = String(
        transaction?.parent_sale_id ??
        transaction?.original_sale_id ??
        transaction?.refunded_sale_id ??
        transaction?.refund_sale_id ??
        detail?.parent_sale_id ??
        detail?.original_sale_id ??
        ''
    ).replace(/^#/, '').trim();
    // const refundLocation = String(
    //     transaction?.outlet_name ??
    //     transaction?.location_name ??
    //     transaction?.registration_outlet_name ??
    //     transaction?.outlet?.name ??
    //     detail?.outlet_name ??
    //     detail?.location_name ??
    //     ''
    // ).trim();
    const refundAmount = parseMoney(
        transaction?.refund_audit?.refund_amount ??
        detail?.refund_audit?.refund_amount ??
        transaction?.refund_amount ??
        transaction?.grand_total ??
        transaction?.amount ??
        Math.abs(Number(transaction?.credit_delta ?? 0))
    );
    const refundGifts = Array.isArray(transaction?.gifts)
        ? transaction.gifts
        : (Array.isArray(detail?.gifts) ? detail.gifts : []);

    const saleDetailsSection = (
        <section
            className={showSaleReceiptLayout
                ? `${styles.section} ${styles.sectionSaleDetailsReceipt} ${styles.saleDetailsReceiptFooter}`
                : `${styles.section} ${(isExpiry || isReinstatement || isUtilisation || isAdjustment || isRefund) ? styles.saleDetailsPinnedBottom : ''} ${(isExpiry || isReinstatement || isAdjustment || isRefund) ? styles.expiryDetailsSection : ''}`.trim()}
            id="membership-txn-sale-details"
        >
            <div className={showSaleReceiptLayout ? styles.sectionTitleReceipt : styles.sectionTitle}>
                {showSaleReceiptLayout
                    ? 'Sale Details'
                    : (isExpiry ? 'Expiry Details' : (isReinstatement ? 'Reinstatement Details' : (isAdjustment ? 'Adjustment Details' : (isRefund ? 'Refund Details' : ((isMembershipPackage || hasGifts) ? 'Membership Details' : 'Sale Details')))))}
            </div>
            <div className={showSaleReceiptLayout ? `${styles.saleDetailsGrid} ${styles.saleDetailsGridReceipt}` : styles.saleDetailsGrid}>
                {(isExpiry || isReinstatement) && !showSaleReceiptLayout ? (
                    <>
                        <div className={styles.detailIconContainer}>
                            <CalendarIcon size={20} strokeColor="#374151" strokeWidth={1.25} />
                        </div>
                        <div className={styles.detailTextBlock}>
                            <span className={styles.detailDatePrimary}>
                                {formatDate(isExpiry
                                    ? (transaction?.expiry_date || transaction?.credit_expiry_date || transaction?.booking_date || transaction?.date || createdAt)
                                    : (transaction?.reinstatement_date || transaction?.booking_date || transaction?.date || createdAt))}
                            </span>
                        </div>
                        <div className={styles.detailIconContainer}>
                            <UserIcon size={20} variant="outline" color="#374151" strokeWidth={2} />
                        </div>
                        <div className={styles.detailTextBlock}>
                            <span className={styles.detailMetaLine}>
                                {(() => {
                                    const actor = isExpiry
                                        ? 'System generated'
                                        : (createdBy && String(createdBy).trim() !== '' && createdBy !== '-')
                                        ? createdBy
                                        : ((updatedBy && String(updatedBy).trim() !== '' && updatedBy !== '-') ? updatedBy : 'Automated Report');
                                    return (
                                        isExpiry ? (
                                            <span className={styles.detailEmphasis}>{actor}</span>
                                        ) : (
                                            <>
                                                <span className={styles.detailMetaLabel}>Created by</span>{' '}
                                                <span className={styles.detailEmphasis}>{actor}</span>
                                            </>
                                        )
                                    );
                                })()}
                            </span>
                            <span className={styles.detailMetaSub}>
                                <span className={styles.detailMetaLabel}>on </span>
                                <span className={styles.detailMetaDateTime}>{isExpiry ? formatExpiryDateTime(updatedAt || createdAt) : formatDateTimeDetailed(createdAt || updatedAt)}</span>
                            </span>
                        </div>
                        {isReinstatement && (
                            <>
                                <div className={styles.detailIconContainer}>
                                    <UserIcon size={20} variant="outline" color="#374151" strokeWidth={2} />
                                </div>
                                <div className={styles.detailTextBlock}>
                                    <span className={styles.detailMetaLine}>
                                        <span className={styles.detailMetaLabel}>Last updated by</span>{' '}
                                        <span className={styles.detailEmphasis}>
                                            {updatedBy && String(updatedBy).trim() !== '' && updatedBy !== '-' ? updatedBy : createdBy}
                                        </span>
                                    </span>
                                    <span className={styles.detailMetaSub}>
                                        <span className={styles.detailMetaLabel}>on </span>
                                        <span className={styles.detailMetaDateTime}>{formatDateTimeDetailed(updatedAt || createdAt)}</span>
                                    </span>
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {(transaction?.authentication_remark || transaction?.remark) && (
                            <>
                                <div className={styles.detailIconContainer}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '2px' }}>
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                    </svg>
                                </div>
                                <div className={styles.detailTextBlock}>
                                    <span>{transaction?.authentication_remark || transaction?.remark}</span>
                                    {(transaction?.authentication_sub_remark || transaction?.sub_remark) && (
                                        <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '11px', marginTop: '2px' }}>
                                            {transaction?.authentication_sub_remark || transaction?.sub_remark}
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                        <div className={styles.detailIconContainer}>
                            <CalendarIcon size={20} strokeColor="#374151" strokeWidth={1.25} />
                        </div>
                        <div className={styles.detailTextBlock}>
                            <span className={showSaleReceiptLayout ? styles.detailDatePrimaryReceipt : styles.detailDatePrimary}>{formatDate(createdAt)}</span>
                        </div>

                        <div className={styles.detailIconContainer}>
                            <UserIcon size={20} variant="outline" color="#374151" strokeWidth={2} />
                        </div>
                        <div className={styles.detailTextBlock}>
                            <span className={showSaleReceiptLayout ? styles.detailMetaLineReceipt : styles.detailMetaLine}>
                                <span className={showSaleReceiptLayout ? styles.detailMetaLabelReceipt : styles.detailMetaLabel}>{isRefund ? 'Refunded by' : 'Created by'}</span>{' '}
                                {createdBy && String(createdBy).trim() !== '' && createdBy !== '-' ? (
                                    <span className={showSaleReceiptLayout ? styles.detailEmphasisReceipt : styles.detailEmphasis}>{createdBy}</span>
                                ) : (
                                    <span className={showSaleReceiptLayout ? styles.detailMetaLabelReceipt : styles.detailMetaLabel}>-</span>
                                )}
                            </span>
                            <span className={styles.detailMetaSub}>
                                <span className={showSaleReceiptLayout ? styles.detailMetaMutedReceipt : styles.detailMetaLabel}>on </span>
                                <span className={showSaleReceiptLayout ? styles.detailMetaDateTimeReceipt : styles.detailMetaDateTime}>
                                    {isAdjustment ? formatExpiryDateTime(createdAt) : formatDateTimeDetailed(createdAt)}
                                </span>
                            </span>
                        </div>

                        {!isRefund && (
                            <>
                                <div className={styles.detailIconContainer}>
                                    <UserIcon size={20} variant="outline" color="#374151" strokeWidth={2} />
                                </div>
                                <div className={styles.detailTextBlock}>
                                    <span className={showSaleReceiptLayout ? styles.detailMetaLineReceipt : styles.detailMetaLine}>
                                        <span className={showSaleReceiptLayout ? styles.detailMetaLabelReceipt : styles.detailMetaLabel}>Last updated by</span>{' '}
                                        {displayUpdatedBy !== '-' ? (
                                            <span className={showSaleReceiptLayout ? styles.detailEmphasisReceipt : styles.detailEmphasis}>{displayUpdatedBy}</span>
                                        ) : (
                                            <span className={showSaleReceiptLayout ? styles.detailMetaLabelReceipt : styles.detailMetaLabel}>-</span>
                                        )}
                                    </span>
                                    <span className={styles.detailMetaSub}>
                                        <span className={showSaleReceiptLayout ? styles.detailMetaMutedReceipt : styles.detailMetaLabel}>on </span>
                                        <span className={showSaleReceiptLayout ? styles.detailMetaDateTimeReceipt : styles.detailMetaDateTime}>
                                            {isAdjustment ? formatExpiryDateTime(updatedAt) : formatDateTimeDetailed(updatedAt)}
                                        </span>
                                    </span>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </section>
    );

    return (
        <div className={styles.overlay} onClick={onClose} role="presentation">
            <aside className={styles.panel} onClick={(event) => event.stopPropagation()} aria-label="Transaction details">
                <header className={`${styles.header} ${(isAdjustment || isRefund) ? styles.adjustmentHeader : ''}`.trim()}>
                    <button type="button" className={`${styles.closeButton} ${(isAdjustment || isRefund) ? styles.adjustmentCloseButton : ''}`.trim()} onClick={onClose} aria-label="Close">
                        {(isAdjustment || isRefund) ? (
                            <img src={BackIcon} alt="" />
                        ) : (
                            '×'
                        )}
                    </button>
                    <h2 className={showSaleReceiptLayout ? styles.titleReceipt : `${styles.title} ${(isExpiry || isReinstatement || isAdjustment) ? styles.titleExpiry : ''}`.trim()}>
                        {showSaleReceiptLayout ? (
                            `${typeof title === 'string' ? title.trim() : title || ''}${statusSuffix}`
                        ) : (isExpiry || isReinstatement || isAdjustment) ? (
                            <>
                                <span className={styles.titleLead}>{isExpiry ? 'Expiry' : (isReinstatement ? 'Reinstatement' : 'Adjustment')}</span>
                                <span className={styles.titleSecondaryMuted}>{titleIdSuffix}</span>
                            </>
                        ) : (typeof title === 'string' && title.toLowerCase().startsWith('sale')) ? (
                            <>
                                <span className={styles.titleLead}>Sale</span>
                                <span className={styles.titleSecondary}>{title.substring(4)}{statusSuffix}</span>
                            </>
                        ) : (typeof title === 'string' && title.toLowerCase().startsWith('expiry')) ? (
                            <>
                                <span className={styles.titleLead}>Expiry</span>
                                <span className={styles.titleSecondaryMuted}>{title.replace(/^expiry/i, '')}</span>
                            </>
                        ) : (typeof title === 'string' && title.toLowerCase().startsWith('reinstatement')) ? (
                            <>
                                <span className={styles.titleLead}>Reinstatement</span>
                                <span className={styles.titleSecondaryMuted}>{title.replace(/^reinstatement/i, '')}</span>
                            </>
                        ) : (
                            `${title}${statusSuffix}`
                        )}
                    </h2>
                    {typeof onAddNote === 'function' && (
                        <div className={styles.menuWrapper} ref={menuRef}>
                            <button
                                type="button"
                                className={styles.menuTrigger}
                                onClick={() => setIsMenuOpen((prev) => !prev)}
                                aria-label="More options"
                            >
                                <img src={menuIcon} alt="menu" />
                            </button>
                            {isMenuOpen && (
                                <ul className={styles.menuDropdown}>
                                    <li
                                        className={styles.menuItem}
                                        onClick={() => {
                                            setIsMenuOpen(false);
                                            onAddNote();
                                        }}
                                    >
                                        {resolvedNote ? 'Edit Notes' : 'Add Notes'}
                                    </li>
                                </ul>
                            )}
                        </div>
                    )}
                </header>
                <div className={`${styles.body} ${showSaleReceiptLayout ? styles.bodyReceiptColumn : styles.bodyScrollable} ${!showSaleReceiptLayout && (isExpiry || isReinstatement || isUtilisation || isAdjustment) ? styles.bodyExpiryPinned : ''}`.trim()}>
                    {showSaleReceiptLayout ? (
                        <>
                            <div className={styles.bodyReceiptScroll}>
                                <section className={`${styles.customerCard} ${styles.customerCardReceipt}`}>
                                    <div
                                        className={styles.avatar}
                                        style={{ backgroundColor: getConsistentColor(membershipDetail?.user_id, displayName) }}
                                    >
                                        {getInitials(displayName)}
                                        {hasMembershipBadge && (
                                            <span className={styles.memberIcon}>
                                                <PrimiumMemberIcon size={16} />
                                            </span>
                                        )}
                                    </div>

                                    <div className={styles.customerMeta}>
                                        <div className={styles.phoneReceipt}>{displayPhone}</div>
                                        <div className={styles.nameReceipt}>{displayName}</div>
                                        <div className={styles.userIdReceipt}>#{membershipDetail?.user_id ?? '-'}</div>
                                    </div>
                                </section>

                                {resolvedNote && (
                                    <section className={styles.noteSection}>
                                        <div className={styles.noteLabel}>Notes:</div>
                                        <div className={styles.noteText}>{resolvedNote}</div>
                                        {typeof onAddNote === 'function' && (
                                            <button type="button" className={styles.noteEditButton} onClick={onAddNote} aria-label="Edit note">
                                                <img src={editIcon} alt="Edit" className={styles.noteEditIcon} />
                                            </button>
                                        )}
                                    </section>
                                )}

                                <section className={styles.receiptItemSection} aria-label="Line item">
                                    <p className={styles.receiptItemTitle}>{receiptLineLabel}</p>
                                </section>
                                <section className={`${styles.calculationBlock} ${styles.calculationBlockReceipt}`}>
                                    {(() => {
                                        const subtotal = parseMoney(
                                            transaction?.sub_total ??
                                            transaction?.subtotal ??
                                            transaction?.amount ??
                                            transaction?.grand_total
                                        );
                                        const gst = parseMoney(transaction?.gst_amount ?? transaction?.tax_amount);
                                        const showGst = gst > 0.0001;
                                        return (
                                            <>
                                                <div className={styles.calcRowReceipt}>
                                                    <span>Subtotal</span>
                                                    <span>${formatAmount(subtotal)}</span>
                                                </div>
                                                {showGst ? (
                                                    <div className={styles.calcRowReceipt}>
                                                        <span>GST</span>
                                                        <span>${formatAmount(gst)}</span>
                                                    </div>
                                                ) : (
                                                    <div className={styles.calcRowReceipt}>
                                                        <span>GST</span>
                                                        <span>$0.00</span>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </section>
                                <section className={styles.totalRowReceipt}>
                                    <span>Total</span>
                                    <strong>${formatAmount(transaction?.grand_total ?? transaction?.amount)}</strong>
                                </section>
                                {hasPaymentTransactions ? (
                                <section className={`${styles.section} ${styles.paymentSectionReceipt}`} ref={paymentMenuRef}>
                                    <p className={styles.transactionsHeading}>Transactions</p>
                                    {displayPayments.map((payment) => {
                                        const key = String(payment?.id ?? `${payment?.payment_method_name || payment?.payment_method}-${payment?.amount_value}`);
                                        return (
                                            <div key={key} className={styles.paymentContainer}>
                                                <div className={`${styles.paymentRow} ${styles.paymentRowReceipt}`} style={{ marginBottom: Number(payment?.amount_value) < 0 ? '4px' : '0', position: 'relative' }}>
                                                    <span className={styles.paymentMethod}>{payment?.payment_method_name || payment?.payment_method || '-'}</span>
                                                    <span className={styles.paymentRowSpacer} aria-hidden="true" />
                                                    <button
                                                        type="button"
                                                        className={`${styles.paymentDots} ${styles.paymentDotsReceipt}`}
                                                        aria-label="Payment options"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setOpenPaymentMenuKey((prev) => (prev === key ? null : key));
                                                        }}
                                                    >
                                                        <img src={menuIcon} alt="" className={styles.paymentMenuIcon} />
                                                    </button>
                                                    <span className={`${styles.paymentAmount} ${styles.paymentAmountReceipt}`}>${formatAmount(payment?.amount_value)}</span>
                                                    {openPaymentMenuKey === key && (
                                                        <ul
                                                            className={styles.menuDropdown}
                                                            style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4 }}
                                                        >
                                                            <li
                                                                className={styles.menuItem}
                                                                onClick={() => {
                                                                    setOpenPaymentMenuKey(null);
                                                                    setSelectedPaymentDetail(payment);
                                                                    setIsPaymentDetailSheetOpen(true);
                                                                }}
                                                            >
                                                                View Details
                                                            </li>
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </section>
                                ) : null}
                                {children}
                            </div>
                            {saleDetailsSection}
                        </>
                    ) : (
                        <>
                            {isRefund && refundSaleReference && (
                                <section className={styles.refundReferenceSection}>
                                    <span>This refund is for </span>
                                    <button
                                        type="button"
                                        className={styles.refundSaleLink}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            navigate(`/sales?search=${encodeURIComponent(refundSaleReference)}`);
                                        }}
                                    >
                                        Sale #{refundSaleReference}
                                    </button>
                                </section>
                            )}

                            {/* {isRefund && refundLocation && (
                                <section className={styles.refundLocationSection}>
                                    <span className={styles.refundLocationLabel}>Location:</span>
                                    <span className={styles.refundLocationValue}>{refundLocation}</span>
                                </section>
                            )} */}

                            <section
                                className={`${styles.customerCard} ${
                                    isAdjustment
                                        ? styles.adjustmentCustomerCard
                                        : ((isExpiry || isReinstatement || isRefund) ? styles.expiryCustomerCard : '')
                                }`.trim()}
                            >
                                <div
                                    className={styles.avatar}
                                    style={{ backgroundColor: getConsistentColor(membershipDetail?.user_id, displayName) }}
                                >
                                    {getInitials(displayName)}
                                    {hasMembershipBadge && (
                                        <span className={styles.memberIcon}>
                                            <PrimiumMemberIcon size={16} />
                                        </span>
                                    )}
                                </div>

                                <div className={styles.customerMeta}>
                                    <div className={styles.phone}>{displayPhone}</div>
                                    <div className={styles.name}>{displayName}</div>
                                    <div className={styles.userId}>(#{membershipDetail?.user_id || '-'})</div>
                                </div>
                            </section>

                            {resolvedNote && !isAdjustment && !isRefund && (
                                <section className={styles.noteSection}>
                                    <div className={styles.noteLabel}>Notes:</div>
                                    <div className={styles.noteText}>{resolvedNote}</div>
                                    {typeof onAddNote === 'function' && (
                                        <button type="button" className={styles.noteEditButton} onClick={onAddNote} aria-label="Edit note">
                                            <img src={editIcon} alt="Edit" className={styles.noteEditIcon} />
                                        </button>
                                    )}
                                </section>
                            )}

                            {isExpiry ? (
                                <>
                                    <section className={styles.expirySummaryBlock}>
                                        <div className={styles.expirySummaryTitleRow}>
                                            <h3>{displayActionLabel}</h3>
                                            <div className={styles.expiryAmount}>{formatExpiryCurrency(expiryCreditAmount)}</div>
                                        </div>
                                        <div className={styles.expiryDateRow}>
                                            <span className={styles.expiryDateLabel}>Expired</span>
                                            <span className={styles.expiryDateValue}>
                                                {formatExpiryShortDate(transaction?.expiry_date || transaction?.credit_expiry_date || transaction?.created_at)}
                                            </span>
                                        </div>
                                    </section>

                                    <section className={styles.expirySubtotalRow}>
                                        <span>Subtotal</span>
                                        <span>{formatExpiryCurrency(expiryCreditAmount)}</span>
                                    </section>

                                    <section className={styles.expiryTotalRow}>
                                        <span>Total</span>
                                        <strong>{formatExpiryCurrency(expiryCreditAmount)}</strong>
                                    </section>

                                    <section className={styles.expiryTransactionsSection}>
                                        <p className={styles.transactionsHeading}>Transactions</p>
                                        <div className={styles.expiryPaymentSummary}>
                                            <div className={styles.expiryPaymentMethod}>{expiryTransactionLabel}</div>
                                            <div className={styles.expiryPaymentBalances}>
                                                <span>Closing Balance: ${formatAmount(expiryClosingBalance)}</span>
                                            </div>
                                        </div>
                                    </section>
                                </>
                            ) : isReinstatement ? (
                                <>
                                    <section className={styles.expirySummaryBlock}>
                                        <div className={styles.expirySummaryTitleRow}>
                                            <h3>{displayActionLabel}</h3>
                                            <div className={styles.expiryAmount}>{formatPositiveCurrency(reinstatementCreditAmount)}</div>
                                        </div>
                                        <div className={styles.expiryDateRow}>
                                            <span className={styles.expiryDateLabel}>Expired Credit</span>
                                            <span className={styles.expiryDateValue}>{formatPositiveCurrency(reinstatementExpiredCredit)}</span>
                                        </div>
                                        <div className={styles.expiryDateRow}>
                                            <span className={styles.expiryDateLabel}>Expiry date</span>
                                            <span className={styles.expiryDateValue}>
                                                {formatDate(transaction?.expiry_date || transaction?.credit_expiry_date || transaction?.created_at)}
                                            </span>
                                        </div>
                                    </section>

                                    <section className={styles.expirySubtotalRow}>
                                        <span>Subtotal</span>
                                        <span>{formatPositiveCurrency(reinstatementCreditAmount)}</span>
                                    </section>

                                    <section className={styles.expiryTotalRow}>
                                        <span>Total</span>
                                        <strong>{formatPositiveCurrency(reinstatementCreditAmount)}</strong>
                                    </section>

                                    <section className={styles.expiryTransactionsSection}>
                                        <p className={styles.transactionsHeading}>Transactions</p>
                                        <div className={styles.expiryPaymentSummary}>
                                            <div className={styles.expiryPaymentMethod}>{expiryTransactionLabel}</div>
                                            <div className={styles.expiryPaymentBalances}>
                                                <span>Closing Balance: ${formatAmount(reinstatementClosingBalance)}</span>
                                            </div>
                                        </div>
                                    </section>
                                </>
                            ) : isAdjustment ? (
                                <>
                                    <section className={styles.adjustmentSummaryBlock}>
                                        <div className={styles.adjustmentSummaryTitleRow}>
                                            <h3>Credit Adjustment</h3>
                                            <div className={styles.adjustmentAmount}>
                                                {formatSignedCurrency(adjustmentAmount)}
                                            </div>
                                        </div>
                                    </section>

                                    <section className={styles.adjustmentReasonRow}>
                                        <span className={styles.adjustmentReasonLabel}>Reason</span>
                                        <span className={styles.adjustmentReasonValue}>
                                            {adjustmentReason || '-'}
                                        </span>
                                    </section>

                                    <section className={styles.adjustmentTotalRow}>
                                        <span>Total</span>
                                        <strong>{formatSignedCurrency(adjustmentAmount)}</strong>
                                    </section>

                                    <section className={styles.expiryTransactionsSection}>
                                        <p className={styles.transactionsHeading}>Transactions</p>
                                        <div className={styles.expiryPaymentSummary}>
                                            <div className={styles.expiryPaymentMethod}>{expiryTransactionLabel}</div>
                                            <div className={styles.expiryPaymentBalances}>
                                                {/* <span>Opening Balance: ${formatAmount(adjustmentOpeningBalance)}</span> */}
                                                <span>Closing Balance: ${formatAmount(adjustmentClosingBalance)}</span>
                                            </div>
                                        </div>
                                    </section>
                                </>
                            ) : isRefund ? (
                                <section className={styles.refundLineItemSection}>
                                    <div className={styles.refundLineHeader}>
                                        <h3>Refund Amount (Membership Top-up)</h3>
                                        <div className={styles.refundLineAmount}>${formatAmount(refundAmount)}</div>
                                    </div>
                                    {refundGifts.map((gift, index) => {
                                        const giftStatus = getGiftReturnStatus(gift);
                                        const serial =
                                            gift?.voucher_serial_number ||
                                            gift?.serial_number ||
                                            gift?.voucherSerialNumber ||
                                            '';
                                        return (
                                            <div key={gift?.id ?? `refund-gift-${index}`} className={styles.refundGiftRow}>
                                                <span className={styles.refundGiftLabel}>Gift {String.fromCharCode(65 + index)}</span>
                                                <span className={styles.refundGiftName}>{getGiftDisplayName(gift)}</span>
                                                {giftStatus && (
                                                    <span className={styles.refundGiftStatus}>({giftStatus})</span>
                                                )}
                                                {serial ? (
                                                    <span className={styles.refundGiftSerial}>{serial}</span>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </section>
                            ) : !isUtilisation ? (
                                <section
                                    className={`${styles.summaryBlock} ${showSummaryBeforeFinancialFooter ? styles.summaryBlockBeforeFinancial : ''}`.trim()}
                                >
                                    <div className={styles.summaryTitleRow}>
                                        <h3>{displayActionLabel}</h3>
                                        <div className={styles.amount}>{!(isReinstatement || isExpiry) ? '$' : ''}{formatAmount(transaction?.amount ?? transaction?.grand_total)}</div>
                                    </div>
                                    <div className={styles.kvList} style={(isReinstatement || isExpiry) ? { gridTemplateColumns: '210px 16px 1fr' } : {}}>
                                        <span className={styles.kvLabel}>Membership ID</span>
                                        <span className={styles.kvColon}>:</span>
                                        <span className={styles.kvValue}>{membershipDetail?.membership_number || '-'}</span>

                                        {isReinstatement && (
                                            <>
                                                <span className={styles.kvLabel}>Credit Reinstatement date</span>
                                                <span className={styles.kvColon}>:</span>
                                                <span className={styles.kvValue}>{formatDate(transaction?.reinstatement_date || transaction?.created_at)}</span>
                                            </>
                                        )}

                                        {(isReinstatement || isExpiry) && (
                                            <>
                                                <span className={styles.kvLabel}>Credit Expiry {isExpiry ? 'Date' : 'date'}</span>
                                                <span className={styles.kvColon}>:</span>
                                                <span className={styles.kvValue}>{formatDate(transaction?.expiry_date || transaction?.credit_expiry_date || transaction?.created_at)}</span>
                                            </>
                                        )}

                                        <span className={styles.kvLabel}>Member Name</span>
                                        <span className={styles.kvColon}>:</span>
                                        <span className={styles.kvValue}>{memberName}</span>

                                        {transaction?.insurance_certificate && (
                                            <>
                                                <span className={styles.kvLabel}>Insurance Certificate Number</span>
                                                <span className={styles.kvColon}>:</span>
                                                <span className={styles.kvValue}>{transaction.insurance_certificate}</span>
                                            </>
                                        )}

                                        {!(isMembershipPackage || hasGifts) && (
                                            <>
                                                <span className={styles.kvLabel}>Opening Balance (S$)</span>
                                                <span className={styles.kvColon}>:</span>
                                                <span className={styles.kvValue}>{formatAmount(transaction?.balance_before)}</span>

                                                <span className={styles.kvLabel}>Closing Balance{(isReinstatement || isExpiry) ? '(S$)' : ' (S$)'}</span>
                                                <span className={styles.kvColon}>:</span>
                                                <span className={styles.kvValue}>{formatAmount(transaction?.balance_after)}</span>
                                            </>
                                        )}
                                    </div>
                                </section>
                            ) : (
                                <section className={styles.itemsBlock}>
                                    {transaction?.items?.map((item, idx) => (
                                        <div key={idx} className={styles.serviceItem}>
                                            <div className={styles.serviceName}>{item?.name || item?.item_name || 'Reserved Service'}</div>
                                            {item?.add_on && <div className={styles.serviceMeta}><em>Add-on:</em> {item.add_on}</div>}
                                            {item?.staff_name && (
                                                <div className={styles.serviceMeta}>
                                                    <em>With:</em>
                                                    <span className={styles.staffBadge}>
                                                        <span className={styles.staffAvatar}>{getInitials(item.staff_name)}</span>
                                                        {item.staff_name}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {(!transaction?.items || transaction?.items?.length === 0) && (
                                        <div className={styles.serviceItem}>
                                            <div className={styles.serviceName}>{memberName || 'Reserved Service'}</div>
                                            {!isUtilisation && (
                                                <div className={styles.serviceMeta}><em>Amount:</em> {formatAmount(transaction?.amount ?? transaction?.grand_total)}</div>
                                            )}
                                        </div>
                                    )}
                                </section>
                            )}

                            {(isRefund || isUtilisation) ? (
                                isUtilisation ? (
                                    <>
                                        {(() => {
                                            const subtotalValue = parseMoney(
                                                transaction?.subtotal ??
                                                transaction?.sub_total ??
                                                transaction?.amount
                                            );
                                            const gstValue = parseMoney(transaction?.tax_amount ?? transaction?.gst_amount);
                                            const cashRoundingValue = parseMoney(transaction?.cash_rounding);
                                            const grandTotalValue = parseMoney(transaction?.grand_total);
                                            const resolvedUtilisationTotal = grandTotalValue > 0.0001
                                                ? grandTotalValue
                                                : (subtotalValue + gstValue + cashRoundingValue);
                                            return (
                                                <>
                                        <section className={`${styles.calculationBlock} ${styles.calculationBlockUtilisation}`}>
                                            <div className={styles.calcRow}>
                                                <span>Subtotal</span>
                                                <span>${formatAmount(subtotalValue)}</span>
                                            </div>
                                            <div className={styles.calcRow}>
                                                <span>GST</span>
                                                <span>${formatAmount(gstValue)}</span>
                                            </div>
                                            {Boolean(cashRoundingValue && Number(cashRoundingValue) !== 0) && (
                                                <div className={styles.calcRow}>
                                                    <span>Cash Rounding</span>
                                                    <span>${Number(cashRoundingValue) > 0 ? '+' : ''}{formatAmount(cashRoundingValue)}</span>
                                                </div>
                                            )}
                                        </section>
                                        <section className={`${styles.totalRow} ${styles.totalRowUtilisation}`}>
                                            <span>Total</span>
                                            <strong>${formatAmount(resolvedUtilisationTotal)}</strong>
                                        </section>
                                        {hasPaymentTransactions ? (
                                        <section className={`${styles.section} ${styles.paymentSectionUtilisation}`} ref={paymentMenuRef}>
                                            <p className={styles.transactionsHeading}>Transactions</p>
                                            {displayPayments.map((payment) => {
                                                const key = String(payment?.id ?? `${payment?.payment_method_name || payment?.payment_method}-${payment?.amount_value}`);
                                                return (
                                                    <div key={key} className={styles.paymentContainer}>
                                                        <div className={styles.paymentRow} style={{ marginBottom: Number(payment?.amount_value) < 0 ? '4px' : '8px', position: 'relative' }}>
                                                            <span className={styles.paymentMethod}>{payment?.payment_method_name || payment?.payment_method || '-'}</span>
                                                            <span className={styles.paymentRowSpacer} aria-hidden="true" />
                                                            <button
                                                                type="button"
                                                                className={styles.paymentDots}
                                                                aria-label="Payment options"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    setOpenPaymentMenuKey((prev) => (prev === key ? null : key));
                                                                }}
                                                            >
                                                                <img src={menuIcon} alt="" className={styles.paymentMenuIcon} />
                                                            </button>
                                                            <span className={styles.paymentAmount}>${formatAmount(payment?.amount_value)}</span>
                                                            {openPaymentMenuKey === key && (
                                                                <ul
                                                                    className={styles.menuDropdown}
                                                                    style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4 }}
                                                                >
                                                                    <li
                                                                        className={styles.menuItem}
                                                                        onClick={() => {
                                                                            setOpenPaymentMenuKey(null);
                                                                            setSelectedPaymentDetail(payment);
                                                                            const el = document.getElementById('membership-txn-sale-details');
                                                                            setIsPaymentDetailSheetOpen(true);
                                                                            el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                                                                        }}
                                                                    >
                                                                        View Details
                                                                    </li>
                                                                </ul>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </section>
                                        ) : null}
                                                </>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <>
                                        <section className={styles.refundAmountRow}>
                                            <span>Refund Amount</span>
                                            <strong>${formatAmount(refundAmount)}</strong>
                                        </section>

                                        <section className={styles.refundCalculationBlock}>
                                            <div className={styles.refundCalcRow}>
                                                <span>Subtotal</span>
                                                <span>${formatAmount(refundAmount)}</span>
                                            </div>
                                            <div className={styles.refundTotalRow}>
                                                <span>Total Refund</span>
                                                <strong>${formatAmount(refundAmount)}</strong>
                                            </div>
                                        </section>

                                        {hasPaymentTransactions ? (
                                            <section className={`${styles.section} ${styles.refundPaymentSection}`} ref={paymentMenuRef}>
                                                <p className={styles.transactionsHeading}>Transactions</p>
                                                {effectiveDisplayPayments.map((payment) => {
                                                    const methodLabel = payment?.payment_method_name || payment?.payment_method || '-';
                                                    const key = String(payment?.id ?? `${methodLabel}-${payment?.amount_value}`);
                                                    const amountValue = Math.abs(Number(payment?.amount_value ?? payment?.amount ?? refundAmount));
                                                    const displayMethod = String(methodLabel).toLowerCase().startsWith('refund')
                                                        ? methodLabel
                                                        : `Refund ${methodLabel}`;
                                                    return (
                                                        <div key={key} className={styles.paymentContainer}>
                                                            <div className={`${styles.paymentRow} ${styles.refundPaymentRow}`} style={{ position: 'relative' }}>
                                                        <span className={styles.paymentMethod}>{displayMethod}</span>
                                                        <span className={styles.paymentRowSpacer} aria-hidden="true" />
                                                                <button
                                                                    type="button"
                                                                    className={styles.paymentDots}
                                                                    aria-label="Payment options"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        setOpenPaymentMenuKey((prev) => (prev === key ? null : key));
                                                                    }}
                                                                >
                                                                    <img src={menuIcon} alt="" className={styles.paymentMenuIcon} />
                                                                </button>
                                                                <span className={styles.paymentAmount}>- ${formatAmount(amountValue)}</span>
                                                                {openPaymentMenuKey === key && (
                                                                    <ul
                                                                        className={styles.menuDropdown}
                                                                        style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4 }}
                                                                    >
                                                                        <li
                                                                            className={styles.menuItem}
                                                                            onClick={() => {
                                                                                setOpenPaymentMenuKey(null);
                                                                                setSelectedPaymentDetail(payment);
                                                                                setIsPaymentDetailSheetOpen(true);
                                                                            }}
                                                                        >
                                                                            View Details
                                                                        </li>
                                                                    </ul>
                                                                )}
                                                    </div>
                                                </div>
                                                    );
                                                })}
                                            </section>
                                        ) : null}
                                    </>
                                )
                            ) : (
                                showTotalAndPaymentsFooter && (
                                    <>
                                        <section className={`${styles.totalRow} ${styles.totalRowUtilisation}`}>
                                            <span>{isAdjustment ? 'Total' : 'Total (S$)'}</span>
                                            <strong>
                                                {isAdjustment
                                                    ? `$${formatAmount(transaction?.grand_total ?? transaction?.amount)}`
                                                    : formatAmount(transaction && transaction?.grand_total ? transaction?.grand_total : transaction?.amount)}
                                            </strong>
                                        </section>

                                        {hasPaymentTransactions ? (
                                        <section className={`${styles.section} ${styles.paymentSectionUtilisation}`} ref={paymentMenuRef}>
                                            <p className={styles.transactionsHeading}>Transactions</p>
                                            {displayPayments.map((payment) => {
                                                const key = String(payment?.id ?? `${payment?.payment_method_name || payment?.payment_method}-${payment?.amount_value}`);
                                                return (
                                                    <div key={key} className={styles.paymentContainer}>
                                                        <div className={styles.paymentRow} style={{ marginBottom: Number(payment?.amount_value) < 0 ? '4px' : '8px', position: 'relative' }}>
                                                            <span className={styles.paymentMethod}>{payment?.payment_method_name || payment?.payment_method || '-'}</span>
                                                            <span className={styles.paymentRowSpacer} aria-hidden="true" />
                                                            <button
                                                                type="button"
                                                                className={styles.paymentDots}
                                                                aria-label="Payment options"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    setOpenPaymentMenuKey((prev) => (prev === key ? null : key));
                                                                }}
                                                            >
                                                                <img src={menuIcon} alt="" className={styles.paymentMenuIcon} />
                                                            </button>
                                                            <span className={styles.paymentAmount}>
                                                                {isAdjustment
                                                                    ? `$${formatAmount(payment?.amount_value)}`
                                                                    : formatAmount(payment?.amount_value)}
                                                            </span>
                                                            {openPaymentMenuKey === key && (
                                                                <ul
                                                                    className={styles.menuDropdown}
                                                                    style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4 }}
                                                                >
                                                                    <li
                                                                        className={styles.menuItem}
                                                                        onClick={() => {
                                                                            setOpenPaymentMenuKey(null);
                                                                            setSelectedPaymentDetail(payment);
                                                                            const el = document.getElementById('membership-txn-sale-details');
                                                                            setIsPaymentDetailSheetOpen(true);
                                                                            el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                                                                        }}
                                                                    >
                                                                        View Details
                                                                    </li>
                                                                </ul>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </section>
                                        ) : null}
                                    </>
                                )
                            )}

                            {hasGifts && !isRefund && (
                                <section className={styles.section} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <div className={styles.giftTitle}>Gift Options</div>
                                    <div className={styles.giftList}>
                                        {transaction.gifts.map((gift, index) => (
                                            <React.Fragment key={index}>
                                                <span className={styles.giftLabel}>Gift {String.fromCharCode(65 + index)}</span>
                                                <span className={styles.giftColon}>:</span>
                                                <span className={styles.giftValue}>{gift.name || gift.gift_name || gift.title}</span>

                                                {(gift.serial_number || gift.voucher_serial_number) && (
                                                    <>
                                                        <span className={styles.giftLabel}>Voucher Serial Number</span>
                                                        <span className={styles.giftColon}>:</span>
                                                        <span className={styles.giftValue}>{gift.serial_number || gift.voucher_serial_number}</span>
                                                    </>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {saleDetailsSection}
                            {children}
                        </>
                    )}
                </div>
                {footer ? (
                    <footer className={styles.panelFooter}>
                        {footer}
                    </footer>
                ) : null}
            </aside>
            {isPaymentDetailSheetOpen && (showSaleReceiptLayout || isUtilisation || isRefund) ? (
                <div className={styles.salePanelNestedBackdrop} aria-hidden="true" />
            ) : null}
            {/* {isPaymentDetailSheetOpen && (showSaleReceiptLayout || isUtilisation || isRefund) && detail ? (
                <MembershipTxnPaymentDetailSheet
                    detail={detail}
                    selectedPayment={selectedPaymentDetail}
                    refundDate={detail?.membership_transaction?.updated_at ?? detail?.updated_at}
                    onClose={() => {
                        setIsPaymentDetailSheetOpen(false);
                        setSelectedPaymentDetail(null);
                    }}
                    onGoToSale={onGoToSale}
                />
            ) : null} */}
        </div>
    );
};

export default MembershipTransactionSidePanel;
